import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DOMPurify from "dompurify";
import { useState } from "react";

import APButton from "../../../components/buttons/APButton";
import APIcon from "../../../components/buttons/APIcon";
import { toDollars } from "../../../helpers/currencies";
import { formatDate } from "../../../helpers/dates";
import {
  getContactCRMUrl,
  getVehicleDealerCRMUrl,
  getVehiclePricingRequestCRMUrl,
} from "../../../helpers/dossiers";
import { replaceNewLinesWithBr, stripSpaces } from "../../../helpers/string";
import { VehiclePricingRequest } from "../../../types/VehiclePricingRequest";

export type VehicleVariantCardActions = {
  onUseQuote?: (pricingRequest: VehiclePricingRequest) => Promise<void>;
};

export type VehicleVariantCardConfig = {
  crmClientURL: string | null;
  showUseQuoteButton: boolean;
  type: "quote-specific" | "generic";
};

const PricingRequestCard = ({
  pricingRequest,
  actions,
  config,
}: {
  pricingRequest: VehiclePricingRequest;
  actions: VehicleVariantCardActions;
  config: VehicleVariantCardConfig;
}) => {
  const [expanded, setExpanded] = useState(false);
  const pricingReceived = pricingRequest.pricingReceived;

  const renderCardRow = (
    left: React.ReactNode | null,
    right: React.ReactNode | null
  ) => {
    if (!left && !right) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          columnGap: 1,
        }}
      >
        <Box>{left}</Box>
        <Box sx={{ alignItems: "flex-end", textAlign: "right" }}>{right}</Box>
      </Box>
    );
  };

  const renderTitle = () => {
    return (
      <>
        {config.crmClientURL && (
          <a
            href={getVehiclePricingRequestCRMUrl(
              config.crmClientURL,
              pricingRequest.id
            )}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
          >
            <Typography variant="titleLarge" color="primary.main">
              {config.type === "quote-specific" &&
                pricingRequest.requestIdentity}
              {config.type === "generic" &&
                `${pricingRequest.vehicleVariant?.year} ${pricingRequest.vehicleVariant?.make} ${pricingRequest.vehicleVariant?.model}`}
            </Typography>
          </a>
        )}
      </>
    );
  };

  const renderFee = () => {
    return (
      <Typography variant="titleMedium" color="onSurface.main">
        Fee:{" "}
        {pricingRequest.procurementFeeExGST
          ? toDollars(pricingRequest.procurementFeeExGST, 0)
          : ""}
      </Typography>
    );
  };

  const renderDiscount = () => {
    return (
      <Typography variant="titleMedium" color="onSurface.main">
        {pricingRequest.discount
          ? `Discount: ${toDollars(pricingRequest.discount, 0)}`
          : ""}
      </Typography>
    );
  };

  const renderDealerName = () => {
    return (
      <>
        {config.crmClientURL && pricingRequest.dealer && (
          <a
            href={getVehicleDealerCRMUrl(
              config.crmClientURL,
              pricingRequest.dealer.id
            )}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
          >
            <Typography variant="titleMedium" color="primary.main">
              {pricingRequest.dealer.name}
            </Typography>
          </a>
        )}
      </>
    );
  };

  const renderDealerPhone = () => {
    const phone = pricingRequest.dealer?.phone;
    return (
      <>
        {phone && (
          <a
            href={`tel:${stripSpaces(phone)}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="titleMedium" color="primary.main">
              {phone}
            </Typography>
          </a>
        )}
      </>
    );
  };

  const renderDealerPrimaryContact = () => {
    const dealer = pricingRequest.dealer;
    return (
      <>
        {dealer?.primaryContactId && config.crmClientURL && (
          <a
            href={getContactCRMUrl(
              config.crmClientURL,
              dealer.primaryContactId
            )}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
          >
            <Typography
              variant="titleMedium"
              color="primary.main"
              sx={{ whiteSpace: "nowrap" }}
            >
              {dealer.primaryContactName}
            </Typography>
          </a>
        )}
      </>
    );
  };

  const renderDate = () => {
    let date = "";
    let tooltipLabel = "";

    if (pricingReceived) {
      date = pricingRequest.pricingReceivedDate
        ? pricingRequest.pricingReceivedDate
        : "";
      tooltipLabel = "Pricing Received Date";
    } else {
      date = pricingRequest.requestSentDate
        ? pricingRequest.requestSentDate
        : "";
      tooltipLabel = "Request Sent Date";
    }
    return (
      <Tooltip title={tooltipLabel}>
        <Typography variant="titleMedium" color="onSurface.main">
          {date ? formatDate(date) : ""}
        </Typography>
      </Tooltip>
    );
  };

  const renderSelected = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          justifyContent: "flex-end",
          height: "1.25rem",
        }}
      >
        {pricingRequest.selected && (
          <>
            <Typography variant="titleMedium" color="secondary.main">
              Selected
            </Typography>
            <Box sx={{ display: "flex" }}>
              <APIcon name="check" color="secondary" size="small" />
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderDetail = (title: string, value: string | null) => {
    if (!value) {
      return null;
    }
    const cleanHtml = DOMPurify.sanitize(replaceNewLinesWithBr(value));

    return (
      <>
        <Box>
          <Typography variant="labelMedium" color="onSurface.main">
            {title}
          </Typography>
          <Typography variant="titleMedium" color="onSurface.main">
            <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <Card
      sx={{
        display: "flex",
        backgroundColor: "surface.main",
        borderColor: pricingRequest.selected ? "primary.main" : undefined,
        flexDirection: "column",
      }}
      variant="outlined"
    >
      <CardActionArea onClick={() => setExpanded(!expanded)}>
        <Stack
          sx={{
            p: {
              xs: 1,
              sm: 2,
            },
          }}
          spacing={1}
        >
          <Box
            sx={{
              display: "flex",
              flexDirecion: "row",
              justifyContent: "space-between",

              width: "100%",
            }}
            pb={{
              xs: 0,
              sm: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {renderTitle()}
            </Box>
            <Box>
              {pricingReceived ? (
                <Tooltip title="Onroad Price">
                  <Typography variant="titleLarge" color="onSurface.main">
                    {pricingRequest.onroadPrice
                      ? toDollars(pricingRequest.onroadPrice, 0)
                      : ""}
                  </Typography>
                </Tooltip>
              ) : (
                renderDate()
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignSelf: "start", width: "100%" }}>
            <Stack spacing={1} sx={{ width: "100%" }}>
              {config.type === "generic" && (
                <Box pb={1}>
                  <Typography variant="titleMedium" color="onSurface.main">
                    {pricingRequest.vehicleVariant?.variant}
                  </Typography>
                </Box>
              )}

              {renderCardRow(
                renderFee(),
                pricingReceived ? renderDiscount() : renderDealerPhone()
              )}

              {renderCardRow(
                renderDealerName(),
                pricingReceived ? renderDate() : renderDealerPrimaryContact()
              )}

              {pricingReceived &&
                pricingRequest.selected &&
                renderCardRow(null, renderSelected())}
            </Stack>
          </Box>

          <Box>
            <Collapse in={expanded}>
              <Stack spacing={1} sx={{ width: "100%" }}>
                {renderCardRow(
                  <Typography variant="titleMedium" color="onSurface.main">
                    {pricingRequest.dealer?.address ??
                      pricingRequest.dealer?.state}
                  </Typography>,
                  pricingReceived && renderDealerPhone()
                )}

                {renderCardRow(
                  pricingReceived
                    ? renderDetail(
                        "Requested Vehicle ETA",
                        pricingRequest.availability
                      )
                    : renderDetail(
                        "Exterior Colour",
                        pricingRequest.requestedExteriorColour
                      ),
                  pricingReceived && renderDealerPrimaryContact()
                )}

                {renderCardRow(
                  pricingReceived
                    ? renderDetail(
                        "Immediate Delivery Vehicles",
                        pricingRequest.immediateDeliveryVehicles
                      )
                    : renderDetail(
                        "Interior Colour",
                        pricingRequest.requestedInteriorColour
                      ),
                  null
                )}

                {renderCardRow(
                  pricingReceived
                    ? renderDetail(
                        "Alternate Vehicles ETA",
                        pricingRequest.alternateVehiclesETA
                      )
                    : renderDetail(
                        "Note to Dealer",
                        pricingRequest.noteToDealer
                      ),
                  null
                )}

                {renderCardRow(
                  pricingReceived
                    ? renderDetail("Dealer Notes", pricingRequest.dealerNotes)
                    : null,
                  null
                )}

                {pricingReceived &&
                  renderDetail(
                    "Options",
                    pricingRequest.options.length > 0
                      ? pricingRequest.options.map((o) => o.name).join(", ")
                      : "None"
                  )}

                {config.showUseQuoteButton &&
                  !!actions.onUseQuote &&
                  !pricingRequest.selected && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <APButton
                        text="Use Quote"
                        variant="contained"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          actions.onUseQuote?.(pricingRequest);
                        }}
                      />
                    </Box>
                  )}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default PricingRequestCard;
